// ManualProjectCard.js

import React, { useState, useEffect } from "react";
import "./ManualProjectCard.css";
import { FaPlay, FaChevronLeft, FaChevronRight, FaExpand, FaTimes } from "react-icons/fa";

// A simple Image Modal Component (no changes here)
const ImageModal = ({ imageUrl, onClose }) => (
  <div className="image-modal-overlay" onClick={onClose}>
    <button className="close-modal-button" onClick={onClose}>
      <FaTimes />
    </button>
    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
      <img src={imageUrl} alt="Enlarged project view" className="enlarged-image" />
    </div>
  </div>
);

export default function ManualProjectCard({ project, theme, onVideoClick }) {
  const [media, setMedia] = useState([]);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState("");

  useEffect(() => {
    const videos = (project.videos || []).map(v => ({ ...v, type: 'video' }));
    const images = (project.images || []).map(i => ({ url: i, type: 'image' }));
    setMedia([...videos, ...images]);
  }, [project]);

  const openUrlInNewTab = (url) => {
    if (!url || url === "#") return;
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };

  const handleMediaClick = (e, mediaItem) => {
    e.stopPropagation();
    if (mediaItem.type === 'video') {
      if (mediaItem.url && mediaItem.url !== "#" && onVideoClick) {
        onVideoClick(mediaItem.url);
      }
    } else if (mediaItem.type === 'image') {
      setModalImageUrl(`${process.env.PUBLIC_URL}/projects/${mediaItem.url}`);
      setIsImageModalOpen(true);
    }
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    const isFirst = currentMediaIndex === 0;
    const newIndex = isFirst ? media.length - 1 : currentMediaIndex - 1;
    setCurrentMediaIndex(newIndex);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    const isLast = currentMediaIndex === media.length - 1;
    const newIndex = isLast ? 0 : currentMediaIndex + 1;
    setCurrentMediaIndex(newIndex);
  };

  // NEW: Function to jump to a specific slide
  const goToSlide = (slideIndex) => {
    setCurrentMediaIndex(slideIndex);
  };

  const currentMedia = media[currentMediaIndex];
  if (!media || media.length === 0) {
      // Render the card without the media header if no images/videos
      // Or you can return a simplified card or null
      // For now, let's keep the text part visible
      // (This part is simplified for clarity, the original code had a conditional block)
  }
  
  if (!currentMedia) return null; // Or render a placeholder card

  return (
    <>
      {isImageModalOpen && <ImageModal imageUrl={modalImageUrl} onClose={() => setIsImageModalOpen(false)} />}
      
      <div
        className="manual-project-card"
        style={{
          border: `1px solid ${theme.secondaryText}`,
          cursor: project.url && project.url !== "#" ? "pointer" : "default",
        }}
        onClick={() => openUrlInNewTab(project.url)}
      >
        <div className="project-image-header">
          {media.length > 1 && (
            <>
              <button className="carousel-arrow left" onClick={goToPrevious}><FaChevronLeft /></button>
              <button className="carousel-arrow right" onClick={goToNext}><FaChevronRight /></button>
            </>
          )}

          <img
            src={`${process.env.PUBLIC_URL}/projects/${currentMedia.type === 'video' ? currentMedia.thumbnail : currentMedia.url}`}
            alt={project.title}
            className="project-image"
          />

          <div className="media-overlay" onClick={(e) => handleMediaClick(e, currentMedia)}>
            {currentMedia.type === 'video' ? (
              <div className="video-play-button" style={{ color: theme.highlight }}>
                <FaPlay className="play-icon" />
              </div>
            ) : (
              <div className="image-expand-button" style={{ color: theme.highlight }}>
                <FaExpand className="expand-icon" />
              </div>
            )}
          </div>

          {/* NEW: Carousel Dots container */}
          {media.length > 1 && (
            <div className="carousel-dots">
              {media.map((_, slideIndex) => (
                <div
                  key={slideIndex}
                  className={`carousel-dot ${currentMediaIndex === slideIndex ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click
                    goToSlide(slideIndex);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="manual-project-text">
          {/* ...The rest of the component is unchanged... */}
          <h3 className="manual-project-title" style={{ color: theme.text }}>
            {project.title}
          </h3>
          <p
            className="manual-project-description"
            style={{ color: theme.secondaryText }}
          >
            {project.description}
          </p>

          {(project.role || project.duration) && (
            <div className="project-meta-info">
              {project.role && (
                <div className="meta-item">
                  <span className="meta-title" style={{ color: theme.text }}>
                    Role:
                  </span>
                  <span
                    className="meta-value"
                    style={{ color: theme.secondaryText }}
                  >
                    {project.role}
                  </span>
                </div>
              )}
              {project.duration && (
                <div className="meta-item">
                  <span className="meta-title" style={{ color: theme.text }}>
                    Duration:
                  </span>
                  <span
                    className="meta-value"
                    style={{ color: theme.secondaryText }}
                  >
                    {project.duration}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="manual-project-technologies">
            {project.technologies.map((tech, i) => (
              <span
                key={i}
                className="tech-badge"
                style={{ color: theme.text, backgroundColor: theme.body }}
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}